import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { entityPath } from './entityPaths';

/**
 * BackButton for detail views.
 *
 * When the view was opened from another entity through an EntityLink, location.state.returnTo
 * says where we came from: the button shows "← {label}" and returns to that entity.
 * returnTo.innerTab, when set, reopens that entity on the given inner tab.
 * When it was opened from a dashboard, returnTo.path is the dashboard URL and the button goes there.
 * returnTo only applies to the entity it was created for (entityType/entityIds), so opening
 * another record from the list later does not show a stale label.
 * Otherwise it falls back to onBack (close the detail and show the list).
 */

const BackButton = ({ onBack, entityType, entityIds = [], fallbackLabel }) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();

  const returnTo = location.state?.returnTo;
  const ids = entityIds.filter((x) => x !== undefined && x !== null).map(String);
  const applies = returnTo
    && (!returnTo.targetType || returnTo.targetType === entityType)
    && (returnTo.targetId == null || ids.includes(String(returnTo.targetId)));

  if (!applies) {
    return (
      <button className="btn-back" onClick={onBack} title={fallbackLabel ? t('backButton.backTo', { label: fallbackLabel }) : t('ui.back')}>
        {fallbackLabel ? `← ${fallbackLabel}` : '←'}
      </button>
    );
  }

  const handleBack = () => {
    const entityUrl = returnTo.page ? entityPath(returnTo.page, returnTo.detailId) : null;
    if (returnTo.path) {
      navigate(returnTo.path);
    } else if (entityUrl) {
      // innerTab reopens the origin on the tab it was left from (e.g. a PO's Tasks tab)
      navigate(entityUrl, returnTo.innerTab ? { state: { innerTab: returnTo.innerTab } } : undefined);
    } else {
      navigate(-1);
    }
  };

  return (
    <button className="btn-back" onClick={handleBack} title={t('backButton.backTo', { label: returnTo.label })}>
      ← {returnTo.label}
    </button>
  );
};

export default BackButton;
